import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { z } from "zod";
import { USERS_QUERIES } from "~/shared/api/users/users.queries";

const MEDIA = "(prefers-color-scheme: dark)";
const isBrowser = typeof window !== "undefined";

export type Theme = "system" | "light" | "dark";
const THEME_DATA_ATTR = "data-theme";

const FALLBACK_THEME = "light";
const ThemeSchema = z.union([z.literal("light"), z.literal("dark")]).catch(FALLBACK_THEME);

export function UserThemePreference({ children }: { children?: React.ReactNode }) {
  const theme = useUserTheme();

  React.useEffect(() => {
    if (!isBrowser) return;

    applyThemeToDOM(theme);
  }, [theme]);

  // Listen to system preference changes only when theme is set to "system" and feature is enabled
  React.useEffect(() => {
    if (!isBrowser || theme !== "system") return;

    function handleMediaQuery(_e: MediaQueryListEvent | MediaQueryList) {
      applyThemeToDOM("system");
    }

    const media = window.matchMedia(MEDIA);

    // Intentionally use deprecated listener methods to support iOS & old browsers
    media.addListener(handleMediaQuery);

    return () => media.removeListener(handleMediaQuery);
  }, [theme]);

  return <>{children}</>;
}

export function useUserTheme() {
  const { data: user } = useQuery({ ...USERS_QUERIES.profile(), refetchOnWindowFocus: "always" });

  return user?.applicationTheme ?? FALLBACK_THEME;
}

function getSystemTheme(e?: MediaQueryList | MediaQueryListEvent) {
  if (!isBrowser) return FALLBACK_THEME;

  try {
    if (!e) e = window.matchMedia(MEDIA);
    const isDark = e.matches;
    const systemTheme = isDark ? "dark" : "light";
    return systemTheme;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Failed to detect system theme preference:", error);
    return FALLBACK_THEME;
  }
}

function applyThemeToDOM(theme: Theme) {
  const documentElm = document.documentElement;

  const resolved = theme === "system" ? getSystemTheme() : theme;
  const parsed = ThemeSchema.parse(resolved);

  documentElm.setAttribute(THEME_DATA_ATTR, parsed);
}
